/**
 * Per-tab cache for individual chat-history answer items.
 *
 * Each item is uniquely identified by its `chat_key` + `epoch_time_secs`
 * pair. Items whose `status` is `"inprogress"` or `"initializing"` can
 * still mutate server-side and are intentionally NOT cached: every visit
 * must re-fetch them so the UI sees the latest preliminary content.
 * Items with any other status (or with no `status` field at all — legacy
 * rows the backend wrote before the field existed) are considered final
 * and are persisted to `sessionStorage` so subsequent mounts and polls
 * within the same browser session can skip the network round-trip.
 *
 * The cache lives only for the lifetime of the tab. Closing the tab
 * flushes it, which is the right policy when the backend has changed an
 * answer's body (re-run, edit, schema migration). A reload therefore
 * re-fetches each item once before it is allowed to serve from cache.
 *
 * Writes are defensive: a quota / disabled-storage failure must never
 * break the app because caching is purely an optimisation.
 */

// Bumped to v2 in TP-2925 so rows cached by the previous frontend (which
// didn't carry the `attachments` field) are ignored on first read after
// deploy and re-fetched from the backend. Bump again whenever the row
// shape gains a field the UI must read.
const CACHE_KEY_PREFIX = 'tpl-chat-item-v2-';

const NON_FINAL_STATUSES = new Set(['inprogress', 'initializing']);

/**
 * In-memory registry of `(chat_key, epoch_time_secs)` pairs that have
 * reached a terminal state this tab session. Complements sessionStorage:
 * settled rows skip per-item `/chat/history` polls even before a cache
 * write (e.g. bulk load already applied final props).
 */
const sessionSettledAnswerKeys = new Set<string>();

function sessionSettledRegistryKey(
  chatKey: string,
  epochTimeSecs: number,
): string {
  return `${chatKey}:${epochTimeSecs}`;
}

export function markSessionAnswerSettled(
  chatKey: string | null | undefined,
  epochTimeSecs: number | null | undefined,
): void {
  if (!chatKey || typeof epochTimeSecs !== 'number' || epochTimeSecs <= 0) {
    return;
  }
  sessionSettledAnswerKeys.add(
    sessionSettledRegistryKey(chatKey, epochTimeSecs),
  );
}

/** One poll timer per `epoch_time_secs` so concurrent in-flight turns do not cancel each other. */
const sharedPollTimeouts = new Map<
  number,
  ReturnType<typeof setTimeout>
>();

export function scheduleSharedChatHistoryPoll(
  epochTimeSecs: number,
  delayMs: number,
  tick: () => void,
): void {
  const existing = sharedPollTimeouts.get(epochTimeSecs);
  if (existing != null) {
    clearTimeout(existing);
  }
  const timeoutId = setTimeout(() => {
    sharedPollTimeouts.delete(epochTimeSecs);
    tick();
  }, delayMs);
  sharedPollTimeouts.set(epochTimeSecs, timeoutId);
}

export function cancelSharedChatHistoryPoll(
  epochTimeSecs: number,
): void {
  const existing = sharedPollTimeouts.get(epochTimeSecs);
  if (existing == null) {
    return;
  }
  clearTimeout(existing);
  sharedPollTimeouts.delete(epochTimeSecs);
}

export function isSessionAnswerSettled(
  chatKey: string | null | undefined,
  epochTimeSecs: number | null | undefined,
): boolean {
  if (!chatKey || typeof epochTimeSecs !== 'number' || epochTimeSecs <= 0) {
    return false;
  }
  if (
    sessionSettledAnswerKeys.has(
      sessionSettledRegistryKey(chatKey, epochTimeSecs),
    )
  ) {
    return true;
  }
  const cached = getCachedChatHistoryItem(chatKey, epochTimeSecs);
  return isFinalChatHistoryItem(cached);
}

/**
 * Minimal shape the cache layer needs from a chat-history row. Callers
 * are free to pass richer types (`AnswerItemFromHistory`, raw API
 * payloads, etc.); the cache layer only reads `chat_key`,
 * `epoch_time_secs`, and `status` and serialises the rest verbatim.
 */
export interface CacheableChatHistoryItem {
  chat_key?: string;
  epoch_time_secs?: number;
  status?: string;
  answer?: string;
}

/** Returns parsed answer JSON, or ``null`` when ``answer`` is missing or invalid. */
export function tryParseChatHistoryAnswer(
  answer: unknown,
): Record<string, unknown> | null {
  if (typeof answer !== 'string' || answer.trim() === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(answer);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function cacheKey(chatKey: string, epochTimeSecs: number): string {
  return `${CACHE_KEY_PREFIX}${chatKey}-${epochTimeSecs}`;
}

/**
 * True when the item's `status` is final (or absent — legacy rows).
 * Only final items are safe to memoise for the lifetime of the tab.
 */
export function isFinalChatHistoryItem(
  item: CacheableChatHistoryItem | null | undefined,
): boolean {
  if (!item) return false;
  const status = typeof item.status === 'string' ? item.status : '';
  if (!status) return true;
  return !NON_FINAL_STATUSES.has(status);
}

/**
 * Persist `item` to sessionStorage when it carries a usable identity
 * (`chat_key` + numeric `epoch_time_secs`) and a final status. No-op in
 * non-browser environments and when storage is unavailable.
 */
export function cacheChatHistoryItem(
  item: CacheableChatHistoryItem | null | undefined,
): void {
  if (typeof window === 'undefined') return;
  if (!item) return;
  const chatKey = item.chat_key;
  const epoch = item.epoch_time_secs;
  if (!chatKey || typeof epoch !== 'number') return;
  if (!isFinalChatHistoryItem(item)) return;
  if (!tryParseChatHistoryAnswer(item.answer)) return;
  markSessionAnswerSettled(chatKey, epoch);
  try {
    window.sessionStorage.setItem(cacheKey(chatKey, epoch), JSON.stringify(item));
  } catch {
    // Quota exceeded / disabled by user policy / private-browsing. Cache
    // miss is always safe — the network path remains intact.
  }
}

export function removeCachedChatHistoryItem(
  chatKey: string | null | undefined,
  epochTimeSecs: number | null | undefined,
): void {
  if (typeof window === 'undefined') return;
  if (!chatKey || typeof epochTimeSecs !== 'number') return;
  try {
    window.sessionStorage.removeItem(cacheKey(chatKey, epochTimeSecs));
  } catch {
    // ignore
  }
}

export function getCachedChatHistoryItem<
  T extends CacheableChatHistoryItem = CacheableChatHistoryItem,
>(
  chatKey: string | null | undefined,
  epochTimeSecs: number | null | undefined,
): T | null {
  if (typeof window === 'undefined') return null;
  if (!chatKey || typeof epochTimeSecs !== 'number') return null;
  try {
    const raw = window.sessionStorage.getItem(cacheKey(chatKey, epochTimeSecs));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const item = parsed as T;
    if (!tryParseChatHistoryAnswer(item.answer)) {
      removeCachedChatHistoryItem(chatKey, epochTimeSecs);
      return null;
    }
    return item;
  } catch {
    removeCachedChatHistoryItem(chatKey, epochTimeSecs);
    return null;
  }
}

/**
 * Bulk-cache the result of a list-style chat history fetch. Items
 * without the required identity fields or with non-final status are
 * silently skipped — they will just hit the network on their next
 * per-item refresh.
 */
export function cacheChatHistoryItems(
  items: ReadonlyArray<CacheableChatHistoryItem | null | undefined>,
): void {
  if (typeof window === 'undefined') return;
  for (const item of items) {
    cacheChatHistoryItem(item);
  }
}
